import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';

/** Colour treatment. `neutral` is the company-admin look; `blue` is the Way-superuser look. */
export type ImpersonationAccent = 'neutral' | 'blue';

/**
 * Confirmation dialog for starting an impersonation session.
 *
 * Consolidates the previously duplicated `app-impersonation-confirm-modal` (admin) and
 * `app-way-superuser-confirm-modal` (Way superuser), whose templates were byte-identical and
 * whose stylesheets differed only in the title colour. That difference is preserved through the
 * `accent` input, so both call sites keep their exact appearance.
 *
 * Purely presentational: the body is projected, and starting/cancelling the session is the
 * caller's responsibility.
 *
 * @component
 * @selector app-impersonation-confirm-modal
 * @example
 *   <app-impersonation-confirm-modal
 *     titleId="impersonate-staff-title"
 *     title="Impersonate this user?"
 *     accent="blue"
 *     [busy]="starting"
 *     (confirm)="start()"
 *     (cancel)="close()"
 *   >
 *     <p>You will be signed in as this user.</p>
 *   </app-impersonation-confirm-modal>
 *
 * @input {string} titleId - Required. id of the heading, used for aria-labelledby.
 * @input {string} title - Required. Dialog heading.
 * @input {boolean} busy - Disables both buttons and swaps the primary label for busyLabel.
 * @input {string} confirmLabel - Primary button text.
 * @input {string} busyLabel - Primary button text while busy.
 * @input {ImpersonationAccent} accent - Colour treatment; see ImpersonationAccent.
 * @input {string} testId - Root `data-testid`, and the stem for `<testId>-title`,
 *   `<testId>-cancel-button` and `<testId>-confirm-button`. Defaults to
 *   `impersonation-confirm-dialog`.
 * @output {EventEmitter<void>} confirm - The user confirmed.
 * @output {EventEmitter<void>} cancel - The user cancelled.
 */
@Component({
  selector: 'app-impersonation-confirm-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './impersonation-confirm-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./impersonation-confirm-modal.component.scss'],
})
export class ImpersonationConfirmModalComponent {
  @Input({ required: true }) titleId!: string;
  @Input({ required: true }) title!: string;
  @Input() busy = false;
  @Input() confirmLabel = 'Impersonate';
  @Input() busyLabel = 'Opening…';
  @Input() accent: ImpersonationAccent = 'neutral';
  @Input() testId = 'impersonation-confirm-dialog';

  @Output() cancel = new EventEmitter<void>();
  @Output() confirm = new EventEmitter<void>();
}
